/** Place contracted messenger near the Firefly dock with viewport clamping. */
import {
  DEFAULT_POSITION,
  DOCK_POSITION_STORAGE_KEY,
  isHorizontalEdge,
  parseStoredPosition,
  type ActivityDockPosition,
} from "@/components/activity/activityDockPositionMath";
import { FIREFLY_DOCK_HIT_DIAMETER_PX } from "@/components/activity/firefly/fireflyDockGeometry";
import type { CSSProperties } from "react";

const MARGIN_PX = 8;

function readShellSidebarWidthPx(): number {
  if (typeof window === "undefined") return 0;
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue("--shell-sidebar-w")
    .trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) ? n : 0;
}

function readDockPosition(): ActivityDockPosition {
  if (typeof window === "undefined") return DEFAULT_POSITION;
  try {
    return (
      parseStoredPosition(window.localStorage.getItem(DOCK_POSITION_STORAGE_KEY)) ??
      DEFAULT_POSITION
    );
  } catch {
    return DEFAULT_POSITION;
  }
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

/**
 * Fixed placement for the contracted messenger shell — adjacent to Firefly,
 * clamped so the panel stays fully on-screen.
 */
export function messengerNearFireflyStyle(size: {
  width: number;
  height: number;
}): CSSProperties {
  if (typeof window === "undefined") {
    return { position: "fixed", right: MARGIN_PX, bottom: MARGIN_PX, ...size };
  }

  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const sidebarW = readShellSidebarWidthPx();
  const pos = readDockPosition();
  const tab = FIREFLY_DOCK_HIT_DIAMETER_PX;
  const width = Math.min(size.width, Math.max(280, viewportW - sidebarW - MARGIN_PX * 2));
  const height = Math.min(size.height, Math.max(280, viewportH - MARGIN_PX * 2));

  if (isHorizontalEdge(pos.edge)) {
    const usableW = Math.max(0, viewportW - sidebarW - tab);
    const fireflyLeft = sidebarW + usableW * pos.offsetRatio;
    const fireflyCenterX = fireflyLeft + tab / 2;
    const left = clamp(
      fireflyCenterX - width / 2,
      sidebarW + MARGIN_PX,
      Math.max(sidebarW + MARGIN_PX, viewportW - width - MARGIN_PX),
    );
    if (pos.edge === "bottom") {
      return {
        position: "fixed",
        left,
        bottom: tab + MARGIN_PX,
        width,
        height,
        maxHeight: Math.max(280, viewportH - tab - MARGIN_PX * 2),
      };
    }
    return {
      position: "fixed",
      left,
      top: tab + MARGIN_PX,
      width,
      height,
      maxHeight: Math.max(280, viewportH - tab - MARGIN_PX * 2),
    };
  }

  const fireflyTop = (viewportH - tab) * pos.offsetRatio;
  const fireflyCenterY = fireflyTop + tab / 2;
  const top = clamp(
    fireflyCenterY - height / 2,
    MARGIN_PX,
    Math.max(MARGIN_PX, viewportH - height - MARGIN_PX),
  );

  if (pos.edge === "left") {
    return {
      position: "fixed",
      left: sidebarW + tab + MARGIN_PX,
      top,
      width,
      height,
      maxWidth: Math.max(280, viewportW - sidebarW - tab - MARGIN_PX * 2),
    };
  }

  return {
    position: "fixed",
    right: tab + MARGIN_PX,
    top,
    width,
    height,
    maxWidth: Math.max(280, viewportW - tab - MARGIN_PX * 2),
  };
}
